import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    FormControlLabel,
    Switch,
    Stack,
    IconButton,
    CircularProgress,
    Input,
    Card
} from '@mui/material';
import { ArrowLeft, Upload, X, Plus, Trash2, CheckCircle2, HelpCircle } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { postSubCategory, updateSubCategory, getSubCategoryById } from '../../../../Services/CategoryManagement/CategoryManagementApi';
import { useGlobalSnackbar } from '../../../../Components/Common/GlobalSanckbar';
import { generateSlug, normalizeSlugInput, cleanSlugFinal } from "../../../../utils.js/slugUtils";

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        return JSON.parse(val);
    } catch {
        return [];
    }
};

// Validation function to allow only letters, numbers, and spaces (for general fields)
const validateTextField = (value) => {
    // Allow only letters, numbers, and spaces - remove special characters
    const cleaned = value.replace(/[^a-zA-Z0-9\s]/g, '');
    // Remove multiple spaces between words, but keep leading/trailing spaces during typing
    const singleSpaced = cleaned.replace(/\s+/g, ' ');
    return singleSpaced;
};

// Validation function for slug (allows letters, numbers, hyphens, spaces, and special characters)
const validateSlugField = (value) => {
    // Allow all characters (letters, numbers, hyphens, spaces, and special characters)
    // Only normalize multiple spaces
    const singleSpaced = value.replace(/\s+/g, ' ');
    return singleSpaced;
};

const AddEditSubCategories = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const { categoryId: paramCategoryId, id: paramSubCategoryId } = useParams();
    const location = useLocation();
    const editDataFromState = location.state?.subCategory;
    const subCategoryId = paramSubCategoryId || editDataFromState?.id || location.state?.id;
    const categoryId = paramCategoryId || editDataFromState?.categoryId || location.state?.categoryId;
    const isEdit = Boolean(subCategoryId);

    const [pageLoading, setPageLoading] = useState(isEdit);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        heading: '',
        description: '',
        displayOrder: isEdit ? '' : '1',
        status: true,
        subCategoryImage: null,
        subCategoryIcon: null,
        ogImage: null,
        metaTitle: '',
        metaDescription: '',
        metaOutlet: [],
        metaKeywords: '',
        imageAltText: '',
        canonicalUrl: '',
        isIndex: true
    });
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

    // Suitability Items & FAQs state
    const [suitabilityItems, setSuitabilityItems] = useState([]);
    const [newSuitabilityInput, setNewSuitabilityInput] = useState('');

    const [faqs, setFaqs] = useState([]);
    const [newFaqQuestion, setNewFaqQuestion] = useState('');
    const [newFaqAnswer, setNewFaqAnswer] = useState('');

    // Meta Outlet state
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');

    const [fieldErrors, setFieldErrors] = useState({});

    const nameFieldRef = useRef(null);
    const slugFieldRef = useRef(null);
    const headingFieldRef = useRef(null);
    const descriptionFieldRef = useRef(null);
    const displayOrderFieldRef = useRef(null);
    const imageFieldRef = useRef(null);
    const iconFieldRef = useRef(null);
    const ogImageFieldRef = useRef(null);
    const metaTitleFieldRef = useRef(null);
    const metaDescriptionFieldRef = useRef(null);
    const metaOutletFieldRef = useRef(null);
    const metaKeywordsFieldRef = useRef(null);
    const imageAltTextFieldRef = useRef(null);
    const canonicalUrlFieldRef = useRef(null);
    const isIndexFieldRef = useRef(null);

    const [previewImage, setPreviewImage] = useState(null);
    const [previewIcon, setPreviewIcon] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(null);

    useEffect(() => {
        if (subCategoryId) {
            const fetchSubCategory = async () => {
                setPageLoading(true);
                try {
                    const res = await getSubCategoryById(subCategoryId);
                    const data = res?.data || res?.subCategory || res;
                    if (data) {
                        setFormData({
                            name: data.name || '',
                            slug: data.slug ? cleanSlugFinal(data.slug) : (data.name ? generateSlug(data.name) : ''),
                            heading: data.heading || '',
                            description: data.description || '',
                            displayOrder: data.displayOrder !== undefined && data.displayOrder !== null ? String(data.displayOrder) : '1',
                            status: data.status ?? true,
                            subCategoryImage: null,
                            subCategoryIcon: null,
                            ogImage: null,
                            metaTitle: data.metaTitle || '',
                            metaDescription: data.metaDescription || '',
                            metaOutlet: safeParseArray(data.metaOutlet),
                            metaKeywords: data.metaKeywords || '',
                            imageAltText: data.imageAltText || '',
                            canonicalUrl: data.canonicalUrl || '',
                            isIndex: data.isIndex !== undefined ? (data.isIndex === "true" || data.isIndex === true) : true
                        });
                        setIsSlugManuallyEdited(false);
                        setPreviewImage(data.image || null);
                        setPreviewIcon(data.icon || null);
                        setOgImagePreview(data.ogImage || null);
                        setSuitabilityItems(
                            safeParseArray(data.suitabilityItems)
                                .map(item => (typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : String(item || '')))
                                .filter(Boolean)
                        );
                        setFaqs(
                            safeParseArray(data.faqs).map(faq => ({
                                question: typeof faq?.question === 'object' && faq.question !== null ? faq.question.name || '' : (faq?.question || faq?.[0] || ''),
                                answer: typeof faq?.answer === 'object' && faq.answer !== null ? faq.answer.name || '' : (faq?.answer || faq?.[1] || '')
                            }))
                        );
                    }
                } catch (err) {
                    console.error("Error fetching sub-category for edit:", err);
                    showSnackbar("Failed to fetch sub-category details from API", "error");
                } finally {
                    setPageLoading(false);
                }
            };
            fetchSubCategory();
        }
    }, [subCategoryId, showSnackbar]);

    const handleNameChange = (e) => {
        const newName = validateTextField(e.target.value);
        setFormData(prev => {
            const nextData = { ...prev, name: newName };
            if (!isSlugManuallyEdited) {
                nextData.slug = generateSlug(newName);
            }
            return nextData;
        });
        if (fieldErrors.name) {
            setFieldErrors(prev => ({ ...prev, name: null }));
        }
        if (!isSlugManuallyEdited && fieldErrors.slug) {
            setFieldErrors(prev => ({ ...prev, slug: null }));
        }
    };

    const handleSlugChange = (e) => {
        const rawVal = e.target.value;
        const normalizedVal = validateSlugField(rawVal);
        setFormData(prev => ({
            ...prev,
            slug: normalizedVal
        }));
        setIsSlugManuallyEdited(true);
        if (fieldErrors.slug) {
            setFieldErrors(prev => ({ ...prev, slug: null }));
        }
    };

    const handleSlugBlur = () => {
        if (!formData.slug) {
            setFormData(prev => ({ ...prev, slug: '' }));
            return;
        }
        const cleaned = cleanSlugFinal(formData.slug);
        if (cleaned !== formData.slug) {
            setFormData(prev => ({ ...prev, slug: cleaned }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'displayOrder') {
            if (value === "") {
                setFormData(prev => ({
                    ...prev,
                    displayOrder: ""
                }));
                setFieldErrors(prev => ({ ...prev, displayOrder: null }));
                return;
            }
            if (/^\d*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    displayOrder: value
                }));
                if (Number(value) < 1 || Number(value) > 999) {
                    setFieldErrors(prev => ({ ...prev, displayOrder: "Display order must be between 1 and 999." }));
                } else {
                    setFieldErrors(prev => ({ ...prev, displayOrder: null }));
                }
            }
            return;
        }

        // Apply validation to text fields (except description and slug)
        let processedValue = value;
        if (name === 'heading' || name === 'metaTitle' || name === 'imageAltText' || name === 'canonicalUrl') {
            processedValue = validateTextField(value);
        }
        // Slug and description fields accept special characters, only trim spaces
        else if (name === 'slug' || name === 'description') {
            processedValue = value.trim().replace(/\s+/g, ' ');
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e, field) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const inputElement = e.target;

            if (field === 'subCategoryImage' || field === 'subCategoryIcon') {
                if (!file.type.startsWith('image/')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        [field]: 'Only image files are allowed.'
                    }));
                    setFormData(prev => ({
                        ...prev,
                        [field]: null
                    }));
                    if (field === 'subCategoryImage') {
                        setPreviewImage(null);
                    } else {
                        setPreviewIcon(null);
                    }
                    inputElement.value = "";
                    return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                    const img = new Image();
                    img.onload = () => {
                        if (img.width < 400 && img.height < 400) {
                            setFieldErrors(prev => ({
                                ...prev,
                                [field]: `${field === 'subCategoryImage' ? 'Image' : 'Icon'} "${file.name}" is too small. At least one dimension must be ≥ 400px.`
                            }));
                            setFormData(prev => ({
                                ...prev,
                                [field]: null
                            }));
                            if (field === 'subCategoryImage') {
                                setPreviewImage(null);
                            } else {
                                setPreviewIcon(null);
                            }
                            inputElement.value = "";
                        } else {
                            setFormData(prev => ({
                                ...prev,
                                [field]: file
                            }));
                            if (field === 'subCategoryImage') {
                                setPreviewImage(reader.result);
                            } else {
                                setPreviewIcon(reader.result);
                            }
                            setFieldErrors(prev => ({ ...prev, [field]: null }));
                        }
                    };
                    img.src = reader.result;
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleRemoveImage = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: null
        }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
        if (field === 'subCategoryImage') {
            setPreviewImage(null);
        } else if (field === 'subCategoryIcon') {
            setPreviewIcon(null);
        }
    };

    const handleOgImageChange = (e) => {
        const file = e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    ogImage: 'Only image files are allowed.'
                }));
                setFormData(prev => ({ ...prev, ogImage: null }));
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setFieldErrors(prev => ({
                    ...prev,
                    ogImage: 'Image size must be less than 2MB.'
                }));
                setFormData(prev => ({ ...prev, ogImage: null }));
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, ogImage: file }));
                setOgImagePreview(reader.result);
                setFieldErrors(prev => ({ ...prev, ogImage: null }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setFormData(prev => ({ ...prev, ogImage: null }));
        setOgImagePreview(null);
        if (fieldErrors.ogImage) {
            setFieldErrors(prev => ({ ...prev, ogImage: null }));
        }
    };

    // Suitability Item Handlers
    const handleAddSuitabilityItem = () => {
        const trimmed = newSuitabilityInput.trim();
        if (!trimmed) return;
        setSuitabilityItems(prev => [...prev, trimmed]);
        setNewSuitabilityInput('');
    };

    const handleRemoveSuitabilityItem = (index) => {
        setSuitabilityItems(prev => prev.filter((_, idx) => idx !== index));
    };

    // FAQ Handlers
    const handleAddFaq = () => {
        const qTrimmed = newFaqQuestion.trim();
        const aTrimmed = newFaqAnswer.trim();
        if (!qTrimmed || !aTrimmed) {
            showSnackbar("Please fill both Question and Answer for the FAQ", "warning");
            return;
        }
        setFaqs(prev => [...prev, { question: qTrimmed, answer: aTrimmed }]);
        setNewFaqQuestion('');
        setNewFaqAnswer('');
    };

    const handleRemoveFaq = (index) => {
        setFaqs(prev => prev.filter((_, idx) => idx !== index));
    };

    // Meta Outlet Handlers
    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setFormData(prev => ({
            ...prev,
            metaOutlet: [...(prev.metaOutlet || []), trimmed]
        }));
        setNewMetaOutletInput('');
    };

    const handleRemoveMetaOutletItem = (index) => {
        setFormData(prev => ({
            ...prev,
            metaOutlet: prev.metaOutlet.filter((_, idx) => idx !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFieldErrors({});

        const errors = {};

        if (!formData.name || !formData.name.trim()) {
            errors.name = "Sub-category name is required.";
        }

        const finalSlug = cleanSlugFinal(formData.slug);
        if (!finalSlug) {
            errors.slug = "Please provide a sub-category slug.";
        }

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            errors.displayOrder = "Display order must be between 1 and 999.";
        }

        if (!formData.subCategoryImage && !previewImage) {
            errors.subCategoryImage = "Sub-category image is required.";
        }

        if (!formData.subCategoryIcon && !previewIcon) {
            errors.subCategoryIcon = "Sub-category icon is required.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setTimeout(() => {
                if (errors.name) {
                    nameFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.slug) {
                    slugFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.heading) {
                    headingFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.description) {
                    descriptionFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.displayOrder) {
                    displayOrderFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.subCategoryImage) {
                    imageFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.subCategoryIcon) {
                    iconFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.metaTitle) {
                    metaTitleFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.metaDescription) {
                    metaDescriptionFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.metaOutlet) {
                    metaOutletFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.metaKeywords) {
                    metaKeywordsFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.ogImage) {
                    ogImageFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.imageAltText) {
                    imageAltTextFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.canonicalUrl) {
                    canonicalUrlFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (errors.isIndex) {
                    isIndexFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);
            return;
        }

        setLoading(true);

        const data = new FormData();
        data.append('categoryId', categoryId);
        data.append('name', formData.name.trim());
        data.append('slug', finalSlug);
        data.append('heading', formData.heading || '');
        data.append('description', formData.description || '');

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined) {
            data.append('displayOrder', formData.displayOrder);
        }

        data.append('suitabilityItems', JSON.stringify(suitabilityItems));
        data.append('faqs', JSON.stringify(faqs));
        data.append('metaTitle', formData.metaTitle);
        data.append('metaDescription', formData.metaDescription);
        data.append('metaOutlet', JSON.stringify(formData.metaOutlet));
        data.append('metaKeywords', formData.metaKeywords);
        data.append('imageAltText', formData.imageAltText);
        data.append('canonicalUrl', formData.canonicalUrl);
        data.append('isIndex', formData.isIndex ? "true" : "false");

        if (isEdit) {
            data.append('status', formData.status);
            if (formData.subCategoryImage) {
                data.append('subCategoryImage', formData.subCategoryImage);
            }
            if (formData.subCategoryIcon) {
                data.append('subCategoryIcon', formData.subCategoryIcon);
            }
            if (formData.ogImage) {
                data.append('ogImage', formData.ogImage);
            } else if (!ogImagePreview) {
                data.append('ogImage', '');
            }
        } else {
            if (formData.subCategoryImage) {
                data.append('subCategoryImage', formData.subCategoryImage);
            }
            if (formData.subCategoryIcon) {
                data.append('subCategoryIcon', formData.subCategoryIcon);
            }
            if (formData.ogImage) {
                data.append('ogImage', formData.ogImage);
            }
        }

        try {
            if (isEdit) {
                await updateSubCategory(subCategoryId, data);
                showSnackbar("Sub-category updated successfully!", "success");
            } else {
                await postSubCategory(data);
                showSnackbar("Sub-category created successfully!", "success");
            }
            navigate(`/category-management/sub-categories/${categoryId}`);
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData && errorData.details && Array.isArray(errorData.details)) {
                const errorsObj = {};
                errorData.details.forEach(detail => {
                    const fieldName = (detail.field === "image") ? "subCategoryImage" :
                        (detail.field === "icon") ? "subCategoryIcon" :
                        (detail.field === "ogImage" || detail.field === "og_image") ? "ogImage" : detail.field;
                    errorsObj[fieldName] = detail.message;
                });
                setFieldErrors(errorsObj);

                const firstErrorField = errorData.details[0]?.field;
                setTimeout(() => {
                    if (firstErrorField === "name") {
                        nameFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "slug") {
                        slugFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "heading") {
                        headingFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "description") {
                        descriptionFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "displayOrder") {
                        displayOrderFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "subCategoryImage" || firstErrorField === "image") {
                        imageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "subCategoryIcon" || firstErrorField === "icon") {
                        iconFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "metaTitle") {
                        metaTitleFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "metaDescription") {
                        metaDescriptionFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "metaOutlet") {
                        metaOutletFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "metaKeywords") {
                        metaKeywordsFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "ogImage" || firstErrorField === "og_image") {
                        ogImageFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "imageAltText") {
                        imageAltTextFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "canonicalUrl") {
                        canonicalUrlFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    } else if (firstErrorField === "isIndex") {
                        isIndexFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }, 50);

                showSnackbar(errorData.message || "Validation failed", "error");
            } else {
                const message = err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} sub-category`;
                if (message.toLowerCase().includes("slug")) {
                    setFieldErrors(prev => ({ ...prev, slug: message }));
                    slugFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                showSnackbar(message, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#FBFBFB' }}>
                <CircularProgress size={36} sx={{ color: '#6E8964' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    onClick={() => navigate(`/category-management/sub-categories/${categoryId}`)}
                    sx={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '6px',
                        p: 0.75,
                        '&:hover': { backgroundColor: '#F8FAFC' }
                    }}
                >
                    <ArrowLeft size={18} color="#1E293B" />
                </IconButton>
                <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                    {isEdit ? 'Edit Sub Category' : 'Add New Sub Category'}
                </Typography>
            </Box>

            <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: '8px', border: '1px solid #E2E8F0', maxWidth: 740, mx: 'auto', bgcolor: '#FFFFFF' }} elevation={0}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <Box ref={nameFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>
                                Sub Category Name <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter sub-category name"
                                name="name"
                                value={formData.name}
                                onChange={handleNameChange}
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' }
                                    },
                                    '& .MuiFormHelperText-root.Mui-error': {
                                        color: '#EF4444',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={slugFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>
                                Slug <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Please Enter the Slug "
                                name="slug"
                                value={formData.slug}
                                onChange={handleSlugChange}
                                onBlur={handleSlugBlur}
                                error={Boolean(fieldErrors.slug)}
                                helperText={fieldErrors.slug || "Automatically generated from sub category name"}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        fontFamily: 'monospace',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: fieldErrors.slug ? '#EF4444' : '#64748B',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={headingFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>Heading</Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter heading"
                                name="heading"
                                value={formData.heading}
                                onChange={handleInputChange}
                                error={Boolean(fieldErrors.heading)}
                                helperText={fieldErrors.heading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' }
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={descriptionFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>Description</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter sub-category description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                error={Boolean(fieldErrors.description)}
                                helperText={fieldErrors.description}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' }
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={displayOrderFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>Display Order</Typography>
                            <TextField
                                fullWidth
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter display order (1 - 999)"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
                                }}
                                onChange={handleInputChange}
                                onBlur={() => {
                                    if (formData.displayOrder !== "" && (Number(formData.displayOrder) < 1 || Number(formData.displayOrder) > 999)) {
                                        setFieldErrors(prev => ({ ...prev, displayOrder: "Display order must be between 1 and 999." }));
                                    }
                                }}
                                error={Boolean(fieldErrors.displayOrder)}
                                helperText={fieldErrors.displayOrder}
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        color: '#1E293B',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                        '&.Mui-error fieldset': { borderColor: '#EF4444' }
                                    },
                                    '& .MuiFormHelperText-root.Mui-error': {
                                        color: '#EF4444',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                        mt: 0.75,
                                        mx: 0,
                                    }
                                }}
                            />
                        </Box>

                        <Box ref={imageFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>
                                Sub Category Image <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            {!previewImage ? (
                                <Box
                                    component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.subCategoryImage ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                        borderRadius: "6px",
                                        height: 160,
                                        cursor: "pointer",
                                        bgcolor: "#F8FAFC",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            borderColor: fieldErrors.subCategoryImage ? "#EF4444" : "#6E8964",
                                            bgcolor: "#F1F5F9"
                                        }
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/*" }}
                                        sx={{ display: "none" }}
                                        onChange={(e) => handleFileChange(e, 'subCategoryImage')}
                                    />
                                    <Upload size={24} color={fieldErrors.subCategoryImage ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                    <Typography sx={{ color: fieldErrors.subCategoryImage ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                        Click to upload image
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: fieldErrors.subCategoryImage ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                        PNG, JPG or WebP (Max 2MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={previewImage}
                                        alt="Preview"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleRemoveImage('subCategoryImage')}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E2E8F0",
                                            color: "#EF4444",
                                            p: 0.5,
                                            '&:hover': { bgcolor: '#FEE2E2' }
                                        }}
                                        size="small"
                                    >
                                        <X size={15} />
                                    </IconButton>
                                </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                * At least one dimension of the image must be ≥ 400px.
                            </Typography>
                            {fieldErrors.subCategoryImage && (
                                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                    {fieldErrors.subCategoryImage}
                                </Typography>
                            )}
                        </Box>

                        <Box ref={iconFieldRef}>
                            <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>
                                Sub Category Icon <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            {!previewIcon ? (
                                <Box
                                    component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.subCategoryIcon ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                        borderRadius: "6px",
                                        height: 160,
                                        cursor: "pointer",
                                        bgcolor: "#F8FAFC",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            borderColor: fieldErrors.subCategoryIcon ? "#EF4444" : "#6E8964",
                                            bgcolor: "#F1F5F9"
                                        }
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/*" }}
                                        sx={{ display: "none" }}
                                        onChange={(e) => handleFileChange(e, 'subCategoryIcon')}
                                    />
                                    <Upload size={24} color={fieldErrors.subCategoryIcon ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                    <Typography sx={{ color: fieldErrors.subCategoryIcon ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                        Click to upload icon
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: fieldErrors.subCategoryIcon ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                        PNG, JPG or WebP (Max 2MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={previewIcon}
                                        alt="Icon Preview"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleRemoveImage('subCategoryIcon')}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E2E8F0",
                                            color: "#EF4444",
                                            p: 0.5,
                                            '&:hover': { bgcolor: '#FEE2E2' }
                                        }}
                                        size="small"
                                    >
                                        <X size={15} />
                                    </IconButton>
                                </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                * At least one dimension of the icon must be ≥ 400px.
                            </Typography>
                            {fieldErrors.subCategoryIcon && (
                                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                    {fieldErrors.subCategoryIcon}
                                </Typography>
                            )}
                        </Box>

                        {/* Suitability Items Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle2 size={16} color="#6E8964" /> Suitability Items
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Add suitability guidelines or bullet points for this sub-category.
                            </Typography>

                            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                                <TextField
                                    placeholder="Add suitability item..."
                                    value={newSuitabilityInput}
                                    onChange={(e) => setNewSuitabilityInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddSuitabilityItem();
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            bgcolor: '#FFFFFF',
                                            '& fieldset': { borderColor: '#E2E8F0' },
                                            '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<Plus size={16} />}
                                    onClick={handleAddSuitabilityItem}
                                    sx={{
                                        backgroundColor: '#1E293B',
                                        color: '#FFFFFF',
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        px: 2.5,
                                        '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                    }}
                                >
                                    Add
                                </Button>
                            </Stack>

                            <Stack spacing={1}>
                                {suitabilityItems.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            p: 1.25,
                                            backgroundColor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                            • {typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : item}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveSuitabilityItem(idx)}
                                            sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                        >
                                            <Trash2 size={15} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>

                        {/* FAQs Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HelpCircle size={16} color="#6E8964" /> Frequently Asked Questions (FAQs)
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Add FAQs relevant to this sub-category.
                            </Typography>

                            <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', boxShadow: 'none' }}>
                                <Stack spacing={1.5}>
                                    <TextField
                                        label="Question"
                                        placeholder="e.g. What is included in this sub-category?"
                                        value={newFaqQuestion}
                                        onChange={(e) => setNewFaqQuestion(e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFFFFF',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#6E8964' }
                                        }}
                                    />
                                    <TextField
                                        label="Answer"
                                        placeholder="e.g. Includes guided sessions and accommodation."
                                        value={newFaqAnswer}
                                        onChange={(e) => setNewFaqAnswer(e.target.value)}
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFFFFF',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#6E8964' }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={15} />}
                                            onClick={handleAddFaq}
                                            sx={{
                                                backgroundColor: '#6E8964',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                px: 2,
                                                '&:hover': { backgroundColor: '#5A7352', boxShadow: 'none' }
                                            }}
                                        >
                                            Add FAQ
                                        </Button>
                                    </Box>
                                </Stack>
                            </Card>

                            <Stack spacing={1.5}>
                                {faqs.map((faq, idx) => (
                                    <Paper
                                        key={idx}
                                        variant="outlined"
                                        sx={{ p: 1.75, borderRadius: '6px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none' }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ pr: 2 }}>
                                                <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.8rem' }}>
                                                    Q: {typeof faq?.question === 'object' && faq.question !== null ? faq.question.name || '' : (faq?.question || '')}
                                                </Typography>
                                                <Typography sx={{ color: '#64748B', mt: 0.5, fontSize: '0.775rem' }}>
                                                    A: {typeof faq?.answer === 'object' && faq.answer !== null ? faq.answer.name || '' : (faq?.answer || '')}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveFaq(idx)}
                                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                            >
                                                <Trash2 size={15} />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>

                        {isEdit && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.status}
                                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked }))}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#6E8964',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#6E8964',
                                                },
                                            }}
                                        />
                                    }
                                    label={<Typography sx={{ fontWeight: 500, fontSize: '0.825rem', color: "#334155" }}>Active Status</Typography>}
                                />
                            </Box>
                        )}

                        {/* SEO Section */}
                        <Box sx={{ pt: 2, borderTop: '1px solid #F1F5F9' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5 }}>
                                SEO
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                Configure SEO settings for this sub-category.
                            </Typography>

                            <Stack spacing={2.5}>
                                <Box ref={metaTitleFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Meta Title</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter meta title"
                                        name="metaTitle"
                                        value={formData.metaTitle}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.metaTitle)}
                                        helperText={fieldErrors.metaTitle}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={metaDescriptionFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Meta Description</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Enter meta description"
                                        name="metaDescription"
                                        value={formData.metaDescription}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.metaDescription)}
                                        helperText={fieldErrors.metaDescription}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={metaOutletFieldRef}>
                                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle2 size={16} color="#6E8964" /> Meta Outlet (h2 Meta Outlet)
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                        Add meta outlet bullet points for this sub-category.
                                    </Typography>

                                    <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                                        <TextField
                                            placeholder="Add meta outlet item..."
                                            value={newMetaOutletInput}
                                            onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddMetaOutletItem();
                                                }
                                            }}
                                            fullWidth
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.8rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            onClick={handleAddMetaOutletItem}
                                            sx={{
                                                backgroundColor: '#1E293B',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                px: 2.5,
                                                '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Stack>

                                    <Stack spacing={1}>
                                        {(formData.metaOutlet || []).map((item, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 1.25,
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                    • {item}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveMetaOutletItem(idx)}
                                                    sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                >
                                                    <Trash2 size={15} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* <Box ref={metaKeywordsFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Meta Keywords</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter meta keywords"
                                        name="metaKeywords"
                                        value={formData.metaKeywords}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.metaKeywords)}
                                        helperText={fieldErrors.metaKeywords}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box> */}

                                <Box ref={ogImageFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                        OG Image
                                    </Typography>
                                    {!ogImagePreview ? (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: fieldErrors.ogImage ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                borderRadius: "6px",
                                                height: 160,
                                                cursor: "pointer",
                                                bgcolor: "#F8FAFC",
                                                transition: "all 0.2s ease-in-out",
                                                "&:hover": {
                                                    borderColor: fieldErrors.ogImage ? "#EF4444" : "#6E8964",
                                                    bgcolor: "#F1F5F9"
                                                }
                                            }}
                                        >
                                            <Input
                                                type="file"
                                                inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*" }}
                                                sx={{ display: "none" }}
                                                onChange={handleOgImageChange}
                                            />
                                            <Upload size={24} color={fieldErrors.ogImage ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                            <Typography sx={{ color: fieldErrors.ogImage ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                Click to upload OG image
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: fieldErrors.ogImage ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                PNG, JPG or WebP (Max 2MB)
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ position: "relative", width: "100%", height: 160, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            <Box
                                                component="img"
                                                src={ogImagePreview}
                                                alt="OG Image Preview"
                                                sx={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <IconButton
                                                onClick={handleRemoveOgImage}
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    backgroundColor: "#FFFFFF",
                                                    border: "1px solid #E2E8F0",
                                                    color: "#EF4444",
                                                    p: 0.5,
                                                    '&:hover': { bgcolor: '#FEE2E2' }
                                                }}
                                                size="small"
                                            >
                                                <X size={15} />
                                            </IconButton>
                                        </Box>
                                    )}
                                    {fieldErrors.ogImage && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                            {fieldErrors.ogImage}
                                        </Typography>
                                    )}
                                </Box>

                                <Box ref={imageAltTextFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Image Alt Text</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter image alt text"
                                        name="imageAltText"
                                        value={formData.imageAltText}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.imageAltText)}
                                        helperText={fieldErrors.imageAltText}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={canonicalUrlFieldRef}>
                                    <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>Canonical URL</Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter canonical URL"
                                        name="canonicalUrl"
                                        value={formData.canonicalUrl}
                                        onChange={handleInputChange}
                                        error={Boolean(fieldErrors.canonicalUrl)}
                                        helperText={fieldErrors.canonicalUrl}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                color: '#1E293B',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                                '&.Mui-error fieldset': { borderColor: '#EF4444' },
                                            },
                                            '& .MuiFormHelperText-root.Mui-error': {
                                                color: '#EF4444',
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                mt: 0.75,
                                                mx: 0,
                                            }
                                        }}
                                    />
                                </Box>

                                <Box ref={isIndexFieldRef}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(formData.isIndex)}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, isIndex: e.target.checked }));
                                                    if (fieldErrors.isIndex) {
                                                        setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#6E8964',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#6E8964',
                                                    },
                                                }}
                                            />
                                        }
                                        label={<Typography sx={{ fontWeight: 500, fontSize: '0.825rem', color: "#334155" }}>Is Index</Typography>}
                                    />
                                    {fieldErrors.isIndex && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                            {fieldErrors.isIndex}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #F1F5F9' }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/category-management/sub-categories/${categoryId}`)}
                                disabled={loading}
                                sx={{
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    borderColor: '#E2E8F0',
                                    color: '#475569',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    px: 3,
                                    py: 0.85,
                                    '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    bgcolor: '#6E8964',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    px: 3.5,
                                    py: 0.85,
                                    color: '#FFFFFF',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    '&:hover': { bgcolor: '#5A7352', boxShadow: 'none' },
                                    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }
                                }}
                            >
                                {loading ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : (isEdit ? 'Update Sub Category' : 'Add Sub Category')}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditSubCategories;
